import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ExternalLink, TriangleAlert } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChannelIcon } from "@/lib/channel-icons"
import { channelColor } from "@/lib/channel-colors"
import { cn } from "@/lib/utils"
import { MarkPublishedDialog } from "@/components/pieces/dialogs/MarkPublishedDialog"
import { PipelineSubNav } from "../PipelineSubNav"

type FailedPiece = {
  id: number
  name: string
  channel: string
  channel_name: string | null
  channel_type: string | null
  channel_color: string | null
  failed_reason: string | null
  published_url: string | null
  created_at: string
}

// Failed-posts list. Every piece in the `failed` publishing_status. The
// operator moves each piece back into a main lane (queued to schedule /
// scheduled / published), backburners it, or archives it; each action
// removes the piece from this list (the controller redirects back here).
export default function Failed({ pieces }: { pieces: FailedPiece[] }) {
  return (
    <>
      <Head title="Failed posts">
        <meta name="description" content="Posts that failed to publish, awaiting your decision." />
        <meta property="og:title" content="Failed posts" />
        <meta property="og:description" content="Posts that failed to publish, awaiting your decision." />
      </Head>
      <AppShell wide>
        <div className="flex items-end justify-between border-b border-hairline pb-6">
          <h1>Failed posts</h1>
          <Link href="/pipeline" className="text-sm text-ink-muted no-underline hover:text-ink-display">
            Back to pipeline
          </Link>
        </div>

        <PipelineSubNav active="pipeline" />

        {pieces.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No failed posts. Nice.</p>
        ) : (
          <ul className="mt-8 flex flex-col gap-3">
            {pieces.map((p) => (
              <FailedPieceRow key={p.id} piece={p} />
            ))}
          </ul>
        )}
      </AppShell>
    </>
  )
}

function FailedPieceRow({ piece }: { piece: FailedPiece }) {
  const c = channelColor(piece.channel_color)
  const [markPublishedOpen, setMarkPublishedOpen] = useState(false)

  const fire = (name: string) =>
    router.post(`/pieces/${piece.id}/actions/${name}`, {}, { preserveScroll: false })

  return (
    <li className="rounded-md border border-danger/40 bg-danger-faded/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs", c.cardBg, c.cardBorder, c.text)}>
              <ChannelIcon channelType={piece.channel_type} className="h-3 w-3" />
              {piece.channel_name ?? piece.channel}
            </span>
            <Link
              href={`/pieces/${piece.id}`}
              className="truncate font-medium text-ink-display no-underline hover:text-accent-display"
            >
              {piece.name}
            </Link>
          </div>
          {piece.failed_reason && (
            <div className="mt-2 inline-flex items-start gap-1 text-xs text-danger-display">
              <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{piece.failed_reason}</span>
            </div>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="secondary" size="sm">
              Move to…
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[14rem]">
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); fire("reschedule_failed") }}>
              Move to Queued to schedule
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); fire("to_backburner") }}>
              Move to Backburner
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); fire("retry_publish") }}>
              Move to Scheduled (retry)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setMarkPublishedOpen(true) }}>
              Mark as Published…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); fire("archive") }}>
              Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {piece.published_url && (
        <a
          href={piece.published_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs"
        >
          <ExternalLink className="h-3 w-3" />
          {piece.published_url}
        </a>
      )}

      <MarkPublishedDialog
        open={markPublishedOpen}
        onClose={() => setMarkPublishedOpen(false)}
        pieceId={piece.id}
        variant="out_of_band"
        initialUrl={piece.published_url}
      />
    </li>
  )
}
